import {Box, Grid, InputAdornment, Typography} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import api from 'api';
import {useGetApi} from 'common/hooks/useGetApi';
import {usePostApi} from 'common/hooks/usePostApi';
import {castToSend} from 'common/yup/castToSend';
import AsyncContainer from 'components/AsyncContainer';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import {GridCentered} from 'components/GridCentered';
import FormikBrowseFileInput from 'components/inputs/FormikBrowseFileInput';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import FormikNumberField from 'components/inputs/FormikNumberField';
import FormikRadioButton from 'components/inputs/FormikRadioButton';
import FormikSwitch from 'components/inputs/FormikSwitch';
import FormikTextField from 'components/inputs/FormikTextField';
import {LabelText} from 'components/inputs/LabelText';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import {Offset} from 'components/Offset';
import Panel from 'components/Panel';
import {layoutSelectors} from 'features/layout/redux';
import React, {memo} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {SftpAuthRulesNames, userAction} from 'types/diplomat/sftp/SftpAuthRules';
import {
   ISftpServerSettings,
   SftpServerSettings,
   SftpServerSettingsNames,
   validateSftpServerSettings,
} from 'types/diplomat/sftp/SftpServerSettings';
import SetPasswordPoliciesButton from './SetPasswordPoliciesButton';
import FormikCheckboxArrayOrderable from 'components/inputs/FormikCheckboxArrayOrderable';
import CollapsiblePanel from 'components/CollapsiblePanel';
import {DiplomatFeature} from 'types/DiplomatFeature';
import {licenseSelectors} from 'redux/license';
import {enums} from 'types/enums';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';
import FormikMultiselect from 'components/inputs/FormikMultiselect';
import {EdgeGatewayPanel} from './EdgeGatewayPanel';
import {WebServerSettingsNames} from 'types/diplomat/sftp/WebServerSettings';
import FormikDropdown from 'components/inputs/FormikDropdown';

const useStyles = makeStyles((theme) => ({
   checkBoxList: {height: '30rem', overflow: 'auto'},
}));

function RootForm({onClose}: {onClose: Props['onClose']}) {
   const {t} = useTranslation();

   const isFeatureSupported = useSelector(licenseSelectors.isFeatureSupported);
   const license = useSelector(licenseSelectors.getLicense);
   const isEnterprise = license.edition === enums.LicenseEdition.enum.ENTERPRISE;
   const isStandard = license.edition === enums.LicenseEdition.enum.STANDARD;
   const isTrial = license.type === enums.licenseType.enum.TRIAL;
   // const defaultPermissions = usePermissions(SftpServerSettingsNames.defaultPermissions);
   const hostKeys = useSelector(layoutSelectors.clientSshFilteredKeys);
   const pgpKeys = useSelector(layoutSelectors.keysOpenPgpSecretKeysSelector);

   const getValue = useFieldValueGetter<ISftpServerSettings>();
   const enabled = getValue('enabled');
   const webClientEnabled = getValue(WebServerSettingsNames.webClientEnabled);
   const timestamp = getValue('timestamp');
   const lastUser = getValue('lastUser');
   const _LOGINS_CHECK = getValue(
      `${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames._LOGINS_CHECK}`
   );
   const _LOCK_USER_ACTION = getValue(
      `${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames._LOCK_USER_ACTION}`
   );
   const _DISABLE_USER_AFTER_CHECK = getValue(
      `${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames._DISABLE_USER_AFTER_CHECK}`
   );
   const _REQUIRE_PASSWORD_CHANGE_EVERY_CHECK = getValue(
      `${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames._REQUIRE_PASSWORD_CHANGE_EVERY_CHECK}`
   );

   return (
      <>
         <Grid container spacing={3}>
            {isStandard && isTrial && (
               <Grid item xs={12}>
                  <Typography style={{textAlign: 'center', color: 'blue'}}>
                     * SFTP Server is an optional add-on component for STANDARD edition.
                  </Typography>
               </Grid>
            )}
            <Grid item md={5}>
               <Panel>
                  <Grid container spacing={1}>
                     <Grid item xs={12}>
                        <FormikBrowseFileInput
                           name={SftpServerSettingsNames.rootDirectory}
                           promptOnChange={t(
                              'You are attempting to change the Server root folder.  Any Filesystem permissions assigned to folders that no longer exist will be removed.  Are you sure you want to proceed?'
                           )}
                           label={t('Root Folder')}
                           directoriesOnly
                        />
                     </Grid>
                     <Grid item md={8} xs={12}>
                        <Panel>
                           <Grid container spacing={1}>
                              <Grid item xs={12}>
                                 <FormikSwitch
                                    label={t('SFTP Server ({{status}})', {
                                       status: enabled ? t('Enabled') : t('Disabled'),
                                    })}
                                    // labels={[t('Enable'), t('Disable')]}
                                    name={SftpServerSettingsNames.enabled}
                                 />
                              </Grid>
                              <Grid item xs={4}>
                                 <FormikTextField
                                    name={SftpServerSettingsNames.address}
                                    label={t('Listen IP')}
                                 />
                              </Grid>
                              <Offset xs={1} />
                              <Grid item xs={4}>
                                 <FormikTextField
                                    name={SftpServerSettingsNames.port}
                                    label={t('Port')}
                                 />
                              </Grid>
                              <Grid item xs={9}>
                                 <FormikMultiselect
                                    name={SftpServerSettingsNames.sshKeyIds}
                                    label={t('SSH Host Keys')}
                                    options={hostKeys}
                                    // groupBy={(option) => option.keyType}
                                 />
                              </Grid>
                           </Grid>
                        </Panel>
                     </Grid>
                     <Grid item md={10} xs={12}>
                        {(isFeatureSupported(DiplomatFeature.WEB_CLIENT) || isEnterprise) && (
                           <Panel>
                              <Grid container spacing={1}>
                                 <Grid item xs={12}>
                                    <FormikSwitch
                                       label={t('Web Transfer Server ({{status}})', {
                                          status: webClientEnabled ? t('Enabled') : t('Disabled'),
                                       })}
                                       // labels={[t('Enable'), t('Disable')]}
                                       name={WebServerSettingsNames.webClientEnabled}
                                    />
                                    {isFeatureSupported(DiplomatFeature.WEB_CLIENT_PGP) && (
                                       <FormikCheckbox
                                          name={WebServerSettingsNames.enforceWebClientPGP}
                                          label={t('Enforce PGP Encryption')}
                                       />
                                    )}
                                 </Grid>

                                 <Grid item xs={7}>
                                    <FormikNumberField
                                       min={1}
                                       label={t('Session Expiration')}
                                       name={WebServerSettingsNames.webClientSessionTimeout}
                                       disabled={!webClientEnabled}
                                       InputProps={{
                                          endAdornment: (
                                             <InputAdornment position="end">
                                                <Typography>{t('Minutes')}</Typography>
                                             </InputAdornment>
                                          ),
                                       }}
                                    />
                                 </Grid>
                                 <Grid item xs={5}>
                                    <FormikDropdown
                                       name={WebServerSettingsNames.webClientPgpKeyId}
                                       disabled={!webClientEnabled}
                                       label={t('PGP Keypair')}
                                       options={pgpKeys}
                                       // groupBy={(option) => option.keyType}
                                    />
                                 </Grid>
                              </Grid>
                           </Panel>
                        )}
                     </Grid>
                  </Grid>
               </Panel>
            </Grid>
            <Offset md={1} />
            <Grid md={5}>
               {isFeatureSupported(DiplomatFeature.EDGE_GATEWAY) && <EdgeGatewayPanel />}
            </Grid>

            <Grid item sm={6}>
               <Panel name={t('Authentication Rules')}>
                  <SetPasswordPoliciesButton prefix={SftpServerSettingsNames.passwordPolicies} />
                  <Grid container spacing={1}>
                     <GridCentered item>
                        <FormikCheckbox
                           label={t('After')}
                           name={`${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames._LOGINS_CHECK}`}
                        />
                     </GridCentered>
                     <Grid item xs={2}>
                        <FormikNumberField
                           min={1}
                           name={`${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames.numInvalidLogins}`}
                           disabled={!_LOGINS_CHECK}
                        />
                     </Grid>
                     <GridCentered item>
                        <LabelText>{t('invalid logins in a')}</LabelText>
                     </GridCentered>
                     <Grid item xs={2}>
                        <FormikNumberField
                           min={1}
                           name={`${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames.invalidLoginPeriod}`}
                           disabled={!_LOGINS_CHECK}
                        />
                     </Grid>
                     <GridCentered item>
                        <LabelText>{t('minute period')}</LabelText>
                     </GridCentered>
                  </Grid>
                  <Box marginLeft={4}>
                     <Grid container spacing={1}>
                        <Grid item>
                           <FormikRadioButton
                              label={t('Lock out user for ')}
                              name={`${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames._LOCK_USER_ACTION}`}
                              value={userAction.enum.LOCK}
                              disabled={!_LOGINS_CHECK}
                           />
                        </Grid>
                        <Grid item xs={2}>
                           <FormikNumberField
                              min={1}
                              name={`${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames.lockoutPeriod}`}
                              disabled={
                                 !_LOGINS_CHECK || _LOCK_USER_ACTION !== userAction.enum.LOCK
                              }
                           />
                        </Grid>
                        <GridCentered item>
                           <LabelText>{t('minutes')}</LabelText>
                        </GridCentered>
                     </Grid>
                     <Grid container spacing={1}>
                        <Grid item>
                           <FormikRadioButton
                              label={t('Disable user')}
                              name={`${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames._LOCK_USER_ACTION}`}
                              value={userAction.enum.DISABLE}
                              disabled={!_LOGINS_CHECK}
                           />
                        </Grid>
                     </Grid>
                  </Box>
                  <Grid container spacing={1}>
                     <GridCentered item>
                        <FormikCheckbox
                           label={t('Disable user after')}
                           name={`${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames._DISABLE_USER_AFTER_CHECK}`}
                        />
                     </GridCentered>
                     <Grid item xs={2}>
                        <FormikNumberField
                           min={1}
                           name={`${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames.inactivityLimit}`}
                           disabled={!_DISABLE_USER_AFTER_CHECK}
                        />
                     </Grid>
                     <GridCentered item>
                        <LabelText>{t('days of inactivity')}</LabelText>
                     </GridCentered>
                  </Grid>
                  <Grid container spacing={1}>
                     <GridCentered item>
                        <FormikCheckbox
                           label={t('Require password change every')}
                           name={`${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames._REQUIRE_PASSWORD_CHANGE_EVERY_CHECK}`}
                        />
                     </GridCentered>
                     <Grid item xs={2}>
                        <FormikNumberField
                           min={1}
                           name={`${SftpServerSettingsNames.authRules}.${SftpAuthRulesNames.passwordChangeLimit}`}
                           disabled={!_REQUIRE_PASSWORD_CHANGE_EVERY_CHECK}
                        />
                     </Grid>
                     <GridCentered item>
                        <LabelText>{t('days')}</LabelText>
                     </GridCentered>
                  </Grid>
               </Panel>
            </Grid>

            <Grid item sm={4}>
               <Panel name={t('SFTP Server Data Retention')}>
                  <Grid container spacing={1}>
                     <Grid item xs={12}>
                        <FormikCheckbox
                           name={SftpServerSettingsNames.deleteOldFiles}
                           label={t('Automatically Delete Old Files')}
                        />
                     </Grid>
                     <Grid item xs={12}>
                        <FormikNumberField
                           name={SftpServerSettingsNames.deleteAgeInDays}
                           label={t('Files older than')}
                           InputProps={{
                              endAdornment: (
                                 <InputAdornment position="end">
                                    <Typography>{t('Days')}</Typography>
                                 </InputAdornment>
                              ),
                           }}
                        />
                     </Grid>
                  </Grid>
               </Panel>
            </Grid>

            <Grid item sm={12}>
               <AdvanceSettings />
            </Grid>
         </Grid>

         <LastUpdatedLabel date={timestamp} author={lastUser} />
         <FormFooterButtons submitLabel={t('Save')} cancel={onClose} skipPermissionCheck />
      </>
   );
}

function AdvanceSettings() {
   const {t} = useTranslation();
   const classes = useStyles();
   const {state: keyExchanges, request: keyExchangesRequest} = useGetApi<string[]>(
      api.keyExchanges
   );
   const {state: ciphers, request: ciphersRequest} = useGetApi<string[]>(api.ciphers);
   const {state: hmac, request: hmacRequest} = useGetApi<string[]>(api.hmacs);
   const {state: compressions, request: compressionsRequest} = useGetApi<string[]>(
      api.compressions
   );

   React.useEffect(() => {
      keyExchangesRequest();
      ciphersRequest();
      hmacRequest();
      compressionsRequest();
   }, [keyExchangesRequest, ciphersRequest, hmacRequest, compressionsRequest]);

   return (
      <CollapsiblePanel name={t('Advanced Settings')}>
         <Grid container spacing={3}>
            <Grid item sm={12 / 5}>
               <Panel name={t('Key Exchanges')}>
                  <div className={classes.checkBoxList}>
                     {keyExchanges ? (
                        <FormikCheckboxArrayOrderable
                           name={SftpServerSettingsNames.keyExchanges}
                           options={keyExchanges}
                        />
                     ) : null}
                  </div>
               </Panel>
            </Grid>

            <Grid item sm={12 / 5}>
               <Panel name={t('Ciphers')}>
                  <div className={classes.checkBoxList}>
                     {ciphers ? (
                        <FormikCheckboxArrayOrderable
                           name={SftpServerSettingsNames.ciphers}
                           options={ciphers}
                        />
                     ) : null}
                  </div>
               </Panel>
            </Grid>

            <Grid item sm={12 / 5}>
               <Panel name={t('HMAC')}>
                  <div className={classes.checkBoxList}>
                     {hmac ? (
                        <FormikCheckboxArrayOrderable
                           name={SftpServerSettingsNames.hmacs}
                           options={hmac}
                        />
                     ) : null}
                  </div>
               </Panel>
            </Grid>

            <Grid item sm={12 / 5}>
               <Panel name={t('Compression')}>
                  <div className={classes.checkBoxList}>
                     {compressions ? (
                        <FormikCheckboxArrayOrderable
                           name={SftpServerSettingsNames.compressions}
                           options={compressions}
                        />
                     ) : null}
                  </div>
               </Panel>
            </Grid>

            <Grid item sm={12 / 5}>
               <Panel name={t('Signature Algorithms')}>
                  <div className={classes.checkBoxList}>
                     <FormikCheckboxArrayOrderable
                        name={SftpServerSettingsNames.signatures}
                        options={enums.signatures.keys}
                     />
                  </div>
               </Panel>
            </Grid>
         </Grid>
      </CollapsiblePanel>
   );
}

interface Props {
   onClose: () => void;
}

const SFTPServerSettingsPanel = memo(({onClose}: Props) => {
   const {t} = useTranslation();
   const {state: settings, request, loading} = useGetApi<ISftpServerSettings>(
      api.sftpServerSettings
   );
   React.useEffect(() => {
      request();
   }, [request]);

   const {request: saveSettings} = usePostApi(`${api.sftpServerSettings}?noWarning=true`);
   const onSubmit = React.useCallback(
      (values: ISftpServerSettings, helpers: FormOnSubmitHelpers<ISftpServerSettings>) => {
         const data = castToSend(SftpServerSettings, values);
         saveSettings(JSON.stringify(data), {
            onSuccess: (data) => {
               helpers.setSubmitting(false);
               onClose();
            },
            onFailure: (err) => {
               helpers.setErrors(err);
               helpers.setSubmitting(false);
            },
         });
      },
      [saveSettings, onClose]
   );

   return (
      <Panel name={t('Server Settings')}>
         <AsyncContainer loading={loading}>
            {settings ? (
               <Formik
                  initialValues={SftpServerSettings.cast(settings)}
                  validationSchema={SftpServerSettings}
                  validate={validateSftpServerSettings}
                  onSubmit={onSubmit}
               >
                  <RootForm onClose={onClose} />
               </Formik>
            ) : null}
         </AsyncContainer>
      </Panel>
   );
});

export default SFTPServerSettingsPanel;
